import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TdDialogService } from '@covalent/core/dialogs';
import { addDays, format } from 'date-fns';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  share,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { TransmissionRunHttpClient } from 'src/app/shared/flight-log-api/transmission-runs/transmission-runs-client.service';
import { FlLoadingService } from 'src/lib/fl-loading/fl-loading.service';
import { sleep } from 'src/util/async';
import {
  getPageIndex,
  getPageQueryParams,
  getPageSize,
  tryGetSkipTakeOrDefault,
} from 'src/util/ui/pagination';
import { AirCarrierViewModel } from 'src/view-models/air-carrier-view-model';
import { TransmissionRunViewModel } from 'src/view-models/transmission-run-view-model';

interface RouteParams {
  searchTerm?: string;
  page?: string;
  pageSize?: string;
}

interface TransmissionRunParams {
  airCarrierId?: string;
  skip?: number;
  take?: number;
  startTime?: Date;
  endTime?: Date;
  searchTerm?: string;
}

@Component({
  selector: 'fl-transmission-run-list-page',
  templateUrl: './transmission-run-list-page.component.html',
  styleUrls: ['./transmission-run-list-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransmissionRunListPageComponent implements OnInit, OnDestroy {
  public transmissionRuns$: Observable<TransmissionRunViewModel[]>;
  public airCarriers$: Observable<AirCarrierViewModel[]>;
  public total$: Observable<number>;

  public displayedColumns = [
    'requestedDate',
    'queuedDate',
    'processedDate',
    'processingState',
    'actions',
  ];

  public page$ = new Subject<PageEvent>();
  public defaultPageSize = 10;
  public pageSizeOptions = [this.defaultPageSize, 20, 40, 80];
  public pageIndex$: Observable<number>;
  public pageSize$: Observable<number | undefined>;
  public isValidDateRange$: Observable<boolean>;

  public maxNoOfDays = 30;

  public _pageRefresh$ = new BehaviorSubject<{}>({});
  public _onDestroy$ = new Subject<void>();

  public loadingKey = 'transmission-runs-table';

  public transmissionRunParameters: UntypedFormGroup;

  public airCarrier$: Observable<AirCarrierViewModel>;

  constructor(
    private _transmissionRun: TransmissionRunHttpClient,
    private _route: ActivatedRoute,
    private _router: Router,
    private _loading: FlLoadingService,
    private _dialogService: TdDialogService,
    private _snackBar: MatSnackBar,
    private _fb: UntypedFormBuilder,
  ) {
    this.airCarrier$ = this._route.data.pipe(
      map((data) => data.airCarrier),
      distinctUntilChanged(),
    );

    const transmissionRunsResults$ = combineLatest([
      this._route.data.pipe(map((data) => data.airCarrier)),
      _route.queryParams,
      this._pageRefresh$,
    ]).pipe(
      switchMap(([airCarrier, queryParams]) => {
        const params = this.parseRouteParams(queryParams, this.defaultPageSize, airCarrier);
        this.markAsLoading();
        return this._transmissionRun.getByAirCarrierId({
          airCarrierId: airCarrier?.id,
          skip: params.skip,
          take: params.take,
          startTime:
            params.startTime !== undefined
              ? format(params.startTime, 'MM-DD-YYYY')
              : params.startTime,
          endTime:
            params.endTime !== undefined ? format(params.endTime, 'MM-DD-YYYY') : params.endTime,
          searchTerm: params.searchTerm,
        });
      }),
      distinctUntilChanged(),
      tap(() => this.markAsLoaded()),
      share(),
    );
    this.transmissionRuns$ = transmissionRunsResults$.pipe(map((r) => r.results));
    this.total$ = transmissionRunsResults$.pipe(map((r) => r.totalCount));
  }

  public ngOnInit() {
    const defaultEndDate = new Date();
    const defaultStartDate = addDays(defaultEndDate, -this.maxNoOfDays);
    this.transmissionRunParameters = this._fb.group({
      searchTerm: undefined,
      startTime: defaultStartDate,
      endTime: defaultEndDate,
    });

    this.transmissionRunParameters.valueChanges
      .pipe(debounceTime(500), takeUntil(this._onDestroy$))
      .subscribe((value: TransmissionRunParams) => {
        const airCarrierAbbreviation = this._route.snapshot.data.airCarrier.abbreviation;
        const params = {
          startTime:
            value.startTime !== undefined ? format(value.startTime, 'MM-DD-YYYY') : undefined,
          endTime: value.endTime !== undefined ? format(value.endTime, 'MM-DD-YYYY') : undefined,
          searchTerm: value.searchTerm ?? undefined,
        };
        this._router.navigate([`/prd/${airCarrierAbbreviation}/transmission-runs`], {
          queryParams: params,
        });
      });

    this.pageIndex$ = getPageIndex(this._route);
    this.pageSize$ = getPageSize(this._route, this.defaultPageSize);
    this.page$.pipe(takeUntil(this._onDestroy$)).subscribe((page) => {
      this.patchQueryParams(getPageQueryParams(page, this.defaultPageSize));
    });
  }

  public onPage(page: PageEvent) {
    this.page$.next(page);
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public markAsLoading() {
    this._loading.register(this.loadingKey);
  }

  public markAsLoaded() {
    this._loading.resolveAll(this.loadingKey);
  }

  private parseRouteParams(
    queryParams: RouteParams,
    defaultPageSize: number,
    airCarrier: AirCarrierViewModel,
  ): TransmissionRunParams {
    return {
      airCarrierId: airCarrier?.id,
      startTime: this.startTime?.value,
      endTime: this.endTime?.value,
      searchTerm: queryParams.searchTerm,
      ...tryGetSkipTakeOrDefault(queryParams, defaultPageSize),
    };
  }

  public async queue(airCarrierId: string) {
    const result = await this._dialogService
      .openConfirm({
        title: 'Queue New Run?',
        message: 'Are you sure to queue a new transmission run?',
        acceptButton: 'Queue',
        cancelButton: 'Cancel',
      })
      .afterClosed()
      .toPromise();

    if (result === true) {
      try {
        const transmissionRun = {
          airCarrierId,
        };
        await this._transmissionRun.create(transmissionRun).toPromise();
        this.markAsLoading();
        await sleep(2000);
        this._snackBar.open(
          'Succesfully queued a transmission run. Please refresh the page if new transmission does not appear.',
          undefined,
          {
            duration: 2000,
          },
        );
      } catch (err) {
        this._snackBar.open('Error queueing transmission run.', undefined, {
          duration: 2000,
        });

        throw err;
      } finally {
        this.markAsLoaded();
      }
    }
  }

  public patchQueryParams<T extends {}>(params: T | undefined) {
    params = params != null ? params : ({} as any);
    const airCarrierAbbreviation = this._route.snapshot.data.airCarrier.abbreviation;
    const currentQueryparams = this._route.snapshot.queryParams;
    const queryParams = {
      ...currentQueryparams,
      ...params,
    };
    this._router.navigate([`/prd/${airCarrierAbbreviation}/transmission-runs`], {
      queryParams: params,
    });
  }

  get startTime() {
    return this.transmissionRunParameters.get('startTime');
  }

  get endTime() {
    return this.transmissionRunParameters.get('endTime');
  }

  public refresh() {
    this._pageRefresh$.next({});
  }
}
